import {useMemo} from "react";
import {useNews} from "../firebase/useNews";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {generateSlug} from "./newsArticleUtils";

/**
 * Custom hook to resolve and retrieve a single news article by ID or slug.
 * Enforces draft protection for non-admin visitors and provides related published articles.
 */
export const useNewsArticle = (identifier) => {
    const isAdmin = useCheckIfAdmin();
    const news = useNews({
        orderBy: "published",
        order: "desc",
    });

    const loading = news === undefined || news === null;

    const matchedArticle = useMemo(() => {
        if (!news || !identifier) return null;

        // 1. Direct ID match
        const byId = news.find((item) => item.id === identifier);
        if (byId) return byId;

        // 2. Slug match
        const normalizedIdentifier = identifier.toLowerCase();
        const bySlug = news.find((item) => {
            const slug = generateSlug(item.title);
            return slug && slug === normalizedIdentifier;
        });

        return bySlug || null;
    }, [news, identifier]);

    const isDraft = matchedArticle?.status === "draft";
    const isDraftBlocked = isDraft && !isAdmin;
    const isNotFound = !loading && !matchedArticle;

    const article = isDraftBlocked ? null : matchedArticle;
    const isAdminPreview = Boolean(isDraft && isAdmin);

    const relatedArticles = useMemo(() => {
        if (!news || !matchedArticle) return [];

        return news
            .filter((item) => item.id !== matchedArticle.id && item.status !== "draft")
            .slice(0, 2);
    }, [news, matchedArticle]);

    return {
        article,
        loading,
        isNotFound,
        isDraftBlocked,
        isAdminPreview,
        relatedArticles,
    };
};
